import { useEffect, useMemo, useState } from 'react';
import { getAvailableCities } from '../utils/backendApi';

export const formatCityLabel = (city) =>
  String(city || '')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const normalizeCityOptions = (cities) => {
  if (!Array.isArray(cities)) return [];

  const seen = new Set();

  return cities.reduce((options, city) => {
    const value = String(city?.name || '').trim();

    if (!value || seen.has(value)) return options;

    seen.add(value);
    options.push({
      value,
      label: formatCityLabel(value),
      country: city?.country ?? null,
      imageUrl: city?.image_url ?? null,
      // The search-area centre, for anything that has to open *somewhere* — the map
      // view. Null for a city configured without coordinates.
      lat: Number.isFinite(city?.lat) ? city.lat : null,
      lon: Number.isFinite(city?.lon) ? city.lon : null,
      // How far out that centre still has events. The same radius ingestion collects
      // within and `/locations/search` marks addresses against, so the "Near me" button
      // cannot draw a different boundary from the one the catalogue has. Required on the
      // response; the null is what a malformed payload degrades to, not a city shape.
      radiusKm: Number.isFinite(city?.radius_km) ? city.radius_km : null
    });

    return options;
  }, []);
};

/**
 * Return the city the user most recently selected, falling back to the city
 * stored during onboarding (`preferences.city`).
 *
 * `preferences.city` is written once at onboarding and is never updated when
 * the user changes cities via the picker, so it drifts out of sync quickly.
 * `lastSelectedCity` is always the authoritative, up-to-date choice.
 *
 * Returns `null` when neither source is available (brand-new session before
 * any city has been persisted).
 */
export const getPreferredCity = (preferences) =>
  localStorage.getItem('lastSelectedCity') || preferences?.city || null;

// When availableCities is empty (fetch failed or no active cities configured),
// we return the raw city param rather than blocking the page. If the city is
// invalid the backend returns 400, which the caller handles as a redirect.
// This is acceptable because an empty city list typically means the API is
// unavailable, in which case all endpoints will fail anyway.
export const resolveAvailableCity = (city, availableCities) => {
  if (!Array.isArray(availableCities) || availableCities.length === 0) return city || '';
  // Match case-insensitively (the backend resolves city names the same way) and
  // return the canonical casing from the list. Falls back to the first active
  // city only when there is genuinely no match.
  const match = city
    ? availableCities.find((c) => String(c).toLowerCase() === String(city).toLowerCase())
    : null;
  return match || availableCities[0];
};

// Module-level cache: every page that mounts this hook in the same browser
// session (onboarding, event-recommendations, search-results, event-details,
// CitySelector) shares one /cities fetch instead of each issuing its own.
// Only a successful, non-empty response is cached — a failed or empty fetch
// is retried on the next mount rather than being remembered as "no cities".
let cachedCityOptions = null;
let inFlightFetch = null;

// Test-only: module scope persists across tests within the same file, so a
// test asserting against different city data needs to clear this first.
export const _resetCityCache = () => {
  cachedCityOptions = null;
  inFlightFetch = null;
};

export function useAvailableCities() {
  const [cityOptions, setCityOptions] = useState(cachedCityOptions || []);
  const [isLoading, setIsLoading] = useState(cachedCityOptions === null);

  useEffect(() => {
    if (cachedCityOptions !== null) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    if (!inFlightFetch) {
      inFlightFetch = getAvailableCities()
        .then((data) => {
          const nextCityOptions = normalizeCityOptions(data);
          if (nextCityOptions.length > 0) {
            cachedCityOptions = nextCityOptions;
          }
          return nextCityOptions;
        })
        .catch(() => [])
        .finally(() => {
          inFlightFetch = null;
        });
    }

    inFlightFetch.then((nextCityOptions) => {
      if (!cancelled) {
        setCityOptions(nextCityOptions);
        setIsLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const cities = useMemo(() => cityOptions.map((city) => city.value), [cityOptions]);

  return { cities, cityOptions, isLoading };
}
